import React from 'react';
import { FormattedMessage } from 'react-intl';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Loader from '@amzn/meridian/loader';
import Input from '@amzn/meridian/input';
import { ModalFooter } from '@amzn/meridian/modal';
import UserBadge from '../UserBadge';
import Icon from '@amzn/meridian/icon';
import closeTokens from '@amzn/meridian-tokens/base/icon/close-small';
import DataState from '../../../../Types/DataState';
import { ProxyRelatedUser } from '../../../../Types';

interface ShareTeamStepProps {
  proxyCoachCount: number;
  proxyCoaches: ProxyRelatedUser[];
  proxyUserName: string;
  addProxyState: (typeof DataState)[keyof typeof DataState];
  removeProxyState: (typeof DataState)[keyof typeof DataState];
  profileState: (typeof DataState)[keyof typeof DataState];
  setProxyUserName: (name: string) => void;
  onRemoveProxy: (proxyUserID: string) => void;
  onAddProxy: () => void;
  onClickFooterButton: () => void;
}

export const ShareTeamStep: React.FC<ShareTeamStepProps> = ({
  proxyCoachCount,
  proxyCoaches,
  proxyUserName,
  addProxyState,
  removeProxyState,
  profileState,
  setProxyUserName,
  onRemoveProxy,
  onAddProxy,
  onClickFooterButton,
}) => (
  <Box width='400px'>
    {proxyCoachCount < 1 && (
      <>
        <Box spacingInset='200 none 400 none'>
          <Text type='h100'>
            <FormattedMessage
              id='cs_perfportal_who_do_i_want_to_assign'
              defaultMessage='Who do I want to assign to be my proxy?'
            />
          </Text>
        </Box>
        <Box spacingInset='none none 400 none'>
          <Input
            id='proxy-user-input'
            data-csa-c-type='element'
            data-csa-c-slot-id='dashboard-proxy-input-field-slot'
            data-csa-c-content-id='dashboard-proxy-input-field'
            value={proxyUserName}
            onChange={setProxyUserName}
            type='text'
            size='large'
            placeholder='alias *'
          />
        </Box>
      </>
    )}

    {proxyCoaches.length > 0 &&
      proxyCoaches.map(proxyCoach => (
        <Box key={proxyCoach.tmEntity.userName}>
          <Row
            spacing='none'
            widths={['grid-2', 'grid-8', 'grid-2']}
            spacingInset='200 300 200 none'
          >
            <Box height='36px' width='36px'>
              <UserBadge
                username={proxyCoach.tmEntity.userName}
                givenName={proxyCoach.tmEntity.displayName}
              />
            </Box>
            <Box>
              <Text>{proxyCoach.tmEntity.displayName}</Text>
              <Text>{proxyCoach.tmEntity.userName}</Text>
            </Box>
            <Box>
              <Button
                data-csa-c-type='button'
                data-csa-c-slot-id='dashboard-proxy-profile-button-slot'
                data-csa-c-content-id='dashboard-proxy-modal-end-share-button'
                data-csa-c-profile-id={`${proxyCoach.tmEntity.userID}`}
                data-csa-c-description='End share team with another TM'
                data-csa-c-tag1='Dashboard'
                type='secondary'
                size='small'
                disabled={removeProxyState === DataState.LOADING}
                onClick={() => onRemoveProxy(proxyCoach.tmEntity.userID)}
              >
                <Icon tokens={closeTokens} />
              </Button>
            </Box>
          </Row>
        </Box>
      ))}

    {(addProxyState === DataState.LOADING ||
      removeProxyState === DataState.LOADING ||
      profileState === DataState.LOADING) && (
      <Row spacingInset='400 none none' alignmentHorizontal='center'>
        <Loader type='circular' size='large' />
      </Row>
    )}

    {proxyCoaches.length === 0 && (
      <Box spacingInset='400 none none'>
        <ModalFooter>
          <Row alignmentHorizontal='end' widths='fit' spacing='300'>
            <Button type='secondary' size='small' onClick={onClickFooterButton}>
              <FormattedMessage
                id='cs_perfportal_close'
                defaultMessage='Close'
              />
            </Button>
            <Button
              type='primary'
              size='small'
              onClick={onAddProxy}
              disabled={
                proxyUserName.trim() === '' ||
                addProxyState === DataState.LOADING
              }
            >
              <FormattedMessage
                id='cs_perfportal_assign_as_my_proxy'
                defaultMessage='Assign as my proxy'
              />
            </Button>
          </Row>
        </ModalFooter>
      </Box>
    )}
  </Box>
);
